import Component from '@bythepixel/component-loader';
import { eventThrottle } from '@Core/utility';
import { breakpoints } from '@Front/constants';

export default class StickyTable extends Component {
  static selector = '[data-comp-sticky-table]';

  GRADIENT_WIDTH = 40;
  MAX_GRADIENT_STOP = 100;

  constructor(containerEl) {
    super(containerEl);
    this.table = containerEl.querySelector('.sticky-table');
    this.tableWrapper = containerEl.querySelector(
      '.sticky-table__table-wrapper',
    );
    this.componentWrapperEl = containerEl;

    this.setInitialTableState();
    this.mount();
  }

  /**
   * Handles initialization requirements for the table elements
   *
   * @returns {void}
   */
  setInitialTableState() {
    const { componentWrapperEl } = this;

    componentWrapperEl.dataset.prevScroll = 0;
    componentWrapperEl.dataset.gradientStop = 0;
    componentWrapperEl.classList.add('not-fully-scrolled');
  }

  /**
   * Determines whether gradient on right side of table should be added
   *
   * @param {HTMLElement} table - the target table element
   * @returns {void}
   */
  updateTables() {
    const { componentWrapperEl, table, tableWrapper } = this;

    const tableWidth = table.offsetWidth;
    const wrapperWidth = tableWrapper.offsetWidth;
    const tableOverflowing = tableWidth > wrapperWidth;

    if (tableOverflowing) {
      componentWrapperEl.classList.add('table-overflow');
      componentWrapperEl.classList.remove('table-no-overflow');

      return;
    }

    componentWrapperEl.classList.add('table-no-overflow');
    componentWrapperEl.classList.remove('table-overflow');
  }

  updateGradient() {
    const { table, tableWrapper, componentWrapperEl } = this;
    const maxScroll = Math.abs(
      Math.floor(tableWrapper.offsetWidth - table.offsetWidth),
    );
    const currentScroll = tableWrapper.scrollLeft;
    const scrollThreshold = Math.floor(maxScroll - this.GRADIENT_WIDTH);
    const prevScroll = parseInt(componentWrapperEl.dataset.prevScroll, 10);
    const gradientStop = parseInt(componentWrapperEl.dataset.gradientStop, 10);
    const reachingScrollEnd = currentScroll >= scrollThreshold;
    const fullyScrolled = currentScroll >= maxScroll - 5;
    const scrollDirection = currentScroll > prevScroll ? 'right' : 'left';

    componentWrapperEl.dataset.prevScroll = currentScroll;
    componentWrapperEl.style.setProperty('--gradientStop', `${gradientStop}%`);
    componentWrapperEl.style.setProperty(
      '--tableHeightMinusScrollbar',
      `${this.table.offsetHeight}px`,
    );

    // If we're not nearing the end of the scroll, ensure our table styles and gradient
    // clearly hint at additional content to the right
    if (!reachingScrollEnd) {
      tableWrapper.style.setProperty('--gradientStop', '0%');
      componentWrapperEl.classList.add('not-fully-scrolled');
      componentWrapperEl.classList.remove('fully-scrolled');
      componentWrapperEl.dataset.gradientStop = 0;

      return;
    }

    // If we're fully scrolled, clear the gradient and update table styles
    if (fullyScrolled) {
      componentWrapperEl.style.setProperty('--gradientStop', '100%');
      componentWrapperEl.classList.add('fully-scrolled');
      componentWrapperEl.classList.remove('not-fully-scrolled');

      return;
    }

    switch (scrollDirection) {
    case 'right':
      if (gradientStop <= this.MAX_GRADIENT_STOP) {
        componentWrapperEl.dataset.gradientStop = gradientStop + 5;
      }
      break;

    case 'left':
      if (gradientStop >= 0) {
        componentWrapperEl.dataset.gradientStop = gradientStop - 5;
      }
      break;

    default:
      break;
    }

    componentWrapperEl.classList.add('not-fully-scrolled');
    componentWrapperEl.classList.remove('fully-scrolled');
  }

  handleWindowResize() {
    this.updateTables();
    this.updateGradient();
  }

  registerListeners() {
    window.addEventListener(
      'resize',
      eventThrottle(() => {
        this.handleWindowResize();
      }),
      500,
    );

    this.tableWrapper.addEventListener('scroll', () => {
      this.updateGradient();
    });
  }

  mount() {
    this.updateGradient();
    this.updateTables();
    this.registerListeners();
  }
}
